import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
  addDoc
} from 'firebase/firestore';
import { db } from './firebase';

// Ring Categories
export const RING_CATEGORIES = {
  MILESTONE: 'milestone',
  PURCHASEABLE: 'purchaseable',
  ADMIN_SPECIAL: 'admin_special'
};

// Ring Sets/Collections
export const RING_SETS = {
  CLASSIC: 'classic',
  NEON: 'neon',
  NATURE: 'nature',
  COSMIC: 'cosmic',
  VINTAGE: 'vintage',
  SEASONAL: 'seasonal',
  PREMIUM: 'premium'
};

export const RING_SET_INFO = {
  [RING_SETS.CLASSIC]: {
    name: 'Classic Collection',
    description: 'Timeless and elegant ring designs',
    icon: '💍'
  },
  [RING_SETS.NEON]: {
    name: 'Neon Collection',
    description: 'Bright and vibrant glowing effects',
    icon: '⚡'
  },
  [RING_SETS.NATURE]: {
    name: 'Nature Collection',
    description: 'Earth-inspired natural elements',
    icon: '🌿'
  },
  [RING_SETS.COSMIC]: {
    name: 'Cosmic Collection',
    description: 'Space and galaxy themed rings',
    icon: '🌌'
  },
  [RING_SETS.VINTAGE]: {
    name: 'Vintage Collection',
    description: 'Retro and nostalgic designs',
    icon: '📻'
  },
  [RING_SETS.SEASONAL]: {
    name: 'Seasonal Collection',
    description: 'Holiday and seasonal themes',
    icon: '🎃'
  },
  [RING_SETS.PREMIUM]: {
    name: 'Premium Collection',
    description: 'Exclusive high-end designs',
    icon: '👑'
  }
};

// Dynamic function to get all ring sets (including custom ones)
export const getAllRingSets = async () => {
  try {
    const customSetsRef = collection(db, 'customRingSets');
    const customSetsSnapshot = await getDocs(customSetsRef);
    
    const customSets = {};
    customSetsSnapshot.forEach(doc => {
      const data = doc.data();
      customSets[doc.id] = doc.id;
    });
    
    return { ...RING_SETS, ...customSets };
  } catch (error) {
    console.error('Error loading custom ring sets:', error);
    return RING_SETS;
  }
};

// Dynamic function to get all ring set info (including custom ones)
export const getAllRingSetInfo = async () => {
  try {
    const customSetsRef = collection(db, 'customRingSets');
    const customSetsSnapshot = await getDocs(customSetsRef);
    
    const customSetInfo = {};
    customSetsSnapshot.forEach(doc => {
      customSetInfo[doc.id] = doc.data();
    });
    
    return { ...RING_SET_INFO, ...customSetInfo };
  } catch (error) {
    console.error('Error loading custom ring set info:', error);
    return RING_SET_INFO;
  }
};

// Ring Rarities
export const RING_RARITIES = {
  common: { color: '#9CA3AF', glow: false, price: 0.99 }, // $0.99
  uncommon: { color: '#10B981', glow: true, price: 1.99 }, // $1.99
  rare: { color: '#3B82F6', glow: true, price: 2.99 }, // $2.99
  epic: { color: '#8B5CF6', glow: true, price: 4.99 }, // $4.99
  legendary: { color: '#F59E0B', glow: true, price: 9.99 }, // $9.99
  special: { color: '#EF4444', glow: true, price: null }, // Not purchaseable
  free: { color: '#6B7280', glow: false, price: 0 } // Free rings
};

// Default Rings Configuration
export const ALL_RINGS = {
  // Milestone Rings - unlocked through achievements
  first_voter: {
    id: 'first_voter',
    name: 'First Voter',
    description: 'Cast your first vote',
    category: RING_CATEGORIES.MILESTONE,
    rarity: 'common',
    cssClass: 'ring-first-voter',
    unlockCondition: (stats) => stats.debates >= 1
  },
  
  debate_enthusiast: {
    id: 'debate_enthusiast',
    name: 'Debate Enthusiast',
    description: 'Voted on 25 debates',
    category: RING_CATEGORIES.MILESTONE,
    rarity: 'uncommon',
    cssClass: 'ring-debate-enthusiast',
    unlockCondition: (stats) => stats.debates >= 25
  },
  
  debate_master: {
    id: 'debate_master',
    name: 'Debate Master',
    description: 'Voted on 100 debates',
    category: RING_CATEGORIES.MILESTONE,
    rarity: 'rare',
    cssClass: 'ring-debate-master',
    unlockCondition: (stats) => stats.debates >= 100
  },
  
  streak_warrior: {
    id: 'streak_warrior',
    name: 'Streak Warrior',
    description: 'Achieved a 7-day streak',
    category: RING_CATEGORIES.MILESTONE,
    rarity: 'rare',
    cssClass: 'ring-streak-warrior',
    unlockCondition: (stats) => stats.streak >= 7
  },
  
  legendary_streaker: {
    id: 'legendary_streaker',
    name: 'Legendary Streaker',
    description: 'Achieved a 30-day streak',
    category: RING_CATEGORIES.MILESTONE,
    rarity: 'legendary',
    cssClass: 'ring-legendary-streaker',
    unlockCondition: (stats) => stats.streak >= 30
  },
  
  social_butterfly: {
    id: 'social_butterfly',
    name: 'Social Butterfly',
    description: 'Gained 50 followers',
    category: RING_CATEGORIES.MILESTONE,
    rarity: 'epic',
    cssClass: 'ring-social-butterfly',
    unlockCondition: (stats) => stats.followers >= 50
  },
  
  influencer: {
    id: 'influencer',
    name: 'Influencer',
    description: 'Gained 100 followers',
    category: RING_CATEGORIES.MILESTONE,
    rarity: 'legendary',
    cssClass: 'ring-influencer',
    unlockCondition: (stats) => stats.followers >= 100
  },

  // Purchaseable Rings - available in store
  gradient_glow: {
    id: 'gradient_glow',
    name: 'Gradient Glow',
    description: 'Colorful gradient with glow effect',
    category: RING_CATEGORIES.PURCHASEABLE,
    rarity: 'uncommon',
    cssClass: 'ring-gradient-glow',
    set: RING_SETS.CLASSIC
  },
  
  pulse_ring: {
    id: 'pulse_ring',
    name: 'Pulse Ring',
    description: 'Animated pulsing border',
    category: RING_CATEGORIES.PURCHASEABLE,
    rarity: 'rare',
    cssClass: 'ring-pulse',
    set: RING_SETS.NEON
  },
  
  rainbow_spiral: {
    id: 'rainbow_spiral',
    name: 'Rainbow Spiral',
    description: 'Animated rainbow spiral effect',
    category: RING_CATEGORIES.PURCHASEABLE,
    rarity: 'epic',
    cssClass: 'ring-rainbow-spiral',
    set: RING_SETS.NEON
  },
  
  diamond_edge: {
    id: 'diamond_edge',
    name: 'Diamond Edge',
    description: 'Sparkling diamond-like border',
    category: RING_CATEGORIES.PURCHASEABLE,
    rarity: 'legendary',
    cssClass: 'ring-diamond-edge',
    set: RING_SETS.PREMIUM
  },

  // Debate-Themed Store Rings
  debate_champion: {
    id: 'debate_champion',
    name: 'Debate Champion',
    description: 'Golden ring for true debate masters',
    category: RING_CATEGORIES.PURCHASEABLE,
    rarity: 'epic',
    cssClass: 'ring-debate-champion',
    set: RING_SETS.PREMIUM
  },
  
  logic_shield: {
    id: 'logic_shield',
    name: 'Logic Shield',
    description: 'Protective barrier of sound reasoning',
    category: RING_CATEGORIES.PURCHASEABLE,
    rarity: 'rare',
    cssClass: 'ring-logic-shield',
    set: RING_SETS.CLASSIC
  },
  
  argument_forge: {
    id: 'argument_forge',
    name: 'Argument Forge',
    description: 'Where strong arguments are crafted',
    category: RING_CATEGORIES.PURCHASEABLE,
    rarity: 'uncommon',
    cssClass: 'ring-argument-forge',
    set: RING_SETS.CLASSIC
  },
  
  truth_seeker: {
    id: 'truth_seeker',
    name: 'Truth Seeker',
    description: 'Always searching for the facts',
    category: RING_CATEGORIES.PURCHASEABLE,
    rarity: 'rare',
    cssClass: 'ring-truth-seeker',
    set: RING_SETS.VINTAGE
  },
  
  wisdom_crown: {
    id: 'wisdom_crown',
    name: 'Wisdom Crown',
    description: 'For those who speak with experience',
    category: RING_CATEGORIES.PURCHASEABLE,
    rarity: 'legendary',
    cssClass: 'ring-wisdom-crown',
    set: RING_SETS.PREMIUM
  },
  
  devil_advocate: {
    id: 'devil_advocate',
    name: 'Devil\'s Advocate',
    description: 'Master of the opposing viewpoint',
    category: RING_CATEGORIES.PURCHASEABLE,
    rarity: 'epic',
    cssClass: 'ring-devil-advocate',
    set: RING_SETS.SEASONAL
  },
  
  fact_checker: {
    id: 'fact_checker',
    name: 'Fact Checker',
    description: 'Verifies claims with precision',
    category: RING_CATEGORIES.PURCHASEABLE,
    rarity: 'uncommon',
    cssClass: 'ring-fact-checker',
    set: RING_SETS.CLASSIC
  },
  
  philosopher_stone: {
    id: 'philosopher_stone',
    name: 'Philosopher\'s Stone',
    description: 'Deep thinker and wisdom seeker',
    category: RING_CATEGORIES.PURCHASEABLE,
    rarity: 'legendary',
    cssClass: 'ring-philosopher-stone',
    set: RING_SETS.PREMIUM
  },
  
  rhetoric_master: {
    id: 'rhetoric_master',
    name: 'Rhetoric Master',
    description: 'Expert in the art of persuasion',
    category: RING_CATEGORIES.PURCHASEABLE,
    rarity: 'epic',
    cssClass: 'ring-rhetoric-master',
    set: RING_SETS.VINTAGE
  },
  
  evidence_vault: {
    id: 'evidence_vault',
    name: 'Evidence Vault',
    description: 'Backed by solid research and data',
    category: RING_CATEGORIES.PURCHASEABLE,
    rarity: 'rare',
    cssClass: 'ring-evidence-vault',
    set: RING_SETS.CLASSIC
  },

  // Admin Special Rings - uploaded by admin
  founder: {
    id: 'founder',
    name: 'Founder',
    description: 'Platform founder exclusive ring',
    category: RING_CATEGORIES.ADMIN_SPECIAL,
    rarity: 'special',
    cssClass: 'ring-founder'
  },
  
  beta_tester: {
    id: 'beta_tester',
    name: 'Beta Tester',
    description: 'Early platform tester',
    category: RING_CATEGORIES.ADMIN_SPECIAL,
    rarity: 'special',
    cssClass: 'ring-beta-tester'
  },
  
  community_hero: {
    id: 'community_hero',
    name: 'Community Hero',
    description: 'Outstanding community contributor',
    category: RING_CATEGORIES.ADMIN_SPECIAL,
    rarity: 'special',
    cssClass: 'ring-community-hero'
  }
};

// Get user's owned rings
export const getUserRings = async (userId) => {
  try {
    const ringsRef = collection(db, 'users', userId, 'rings');
    const ringsSnapshot = await getDocs(ringsRef);
    
    return ringsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      unlockedAt: doc.data().unlockedAt?.toDate() || new Date()
    }));
  } catch (error) {
    console.error('Error getting user rings:', error);
    return [];
  }
};

// Get user's active ring
export const getUserActiveRing = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const activeRing = userData.activeRing || null;
      // Return the user's active ring, or null if none set
      return activeRing;
    }
    
    // For new users, return null (no ring)
    return null;
  } catch (error) {
    console.error('Error getting active ring:', error);
    // On error, return null so users get no ring display
    return null;
  }
};

// Set user's active ring
export const setUserActiveRing = async (userId, ringId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      activeRing: ringId,
      activeRingUpdatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Error setting active ring:', error);
    return false;
  }
};

// Unlock a milestone ring for user
export const unlockMilestoneRing = async (userId, ringId) => {
  try {
    const ringData = ALL_RINGS[ringId];
    if (!ringData || ringData.category !== RING_CATEGORIES.MILESTONE) {
      throw new Error(`Invalid milestone ring: ${ringId}`);
    }

    const ringRef = doc(db, 'users', userId, 'rings', ringId);
    const ringDoc = await getDoc(ringRef);
    
    // Don't unlock if already owned
    if (ringDoc.exists()) {
      return false;
    }

    // Unlock the ring
    await setDoc(ringRef, {
      ...ringData,
      unlockedAt: serverTimestamp(),
      source: 'milestone'
    });

    return true;
  } catch (error) {
    console.error('Error unlocking milestone ring:', error);
    return false;
  }
};

// Purchase a ring for user (real currency)
export const purchaseRing = async (userId, ringId, paymentIntentId = null) => {
  try {
    let ringData = ALL_RINGS[ringId];
    let isCustomRing = false;
    
    // If not in default rings, check custom rings
    if (!ringData) {
      const customRings = await getCustomPurchaseableRings();
      ringData = customRings.find(r => r.id === ringId);
      isCustomRing = true;
    }
    
    if (!ringData || (ringData.category !== RING_CATEGORIES.PURCHASEABLE && ringData.rarity !== 'free')) {
      throw new Error(`Invalid purchaseable ring: ${ringId}`);
    }

    const rarity = RING_RARITIES[ringData.rarity];
    const price = ringData.price !== undefined ? ringData.price : rarity.price;
    
    if (!rarity || (price === null && ringData.rarity !== 'free')) {
      throw new Error(`Ring not purchaseable: ${ringId}`);
    }

    const ringRef = doc(db, 'users', userId, 'rings', ringId);
    const ringDoc = await getDoc(ringRef);
    
    // Don't purchase if already owned
    if (ringDoc.exists()) {
      return { success: false, error: 'Ring already owned' };
    }

    // Free rings don't require payment
    if (price === 0 || ringData.rarity === 'free') {
      await setDoc(ringRef, {
        ...ringData,
        unlockedAt: serverTimestamp(),
        source: 'free_claim',
        purchasePrice: 0,
        isCustom: isCustomRing
      });

      return { success: true, price: 0 };
    }

    // For paid rings, validate payment intent (this would integrate with Stripe)
    if (!paymentIntentId) {
      return { success: false, error: 'Payment required', price: price };
    }

    // Record the purchase
    await setDoc(ringRef, {
      ...ringData,
      unlockedAt: serverTimestamp(),
      source: 'purchase',
      purchasePrice: price,
      paymentIntentId: paymentIntentId,
      isCustom: isCustomRing
    });

    // Log the transaction
    await addDoc(collection(db, 'ringPurchases'), {
      userId,
      ringId,
      ringName: ringData.name,
      price,
      paymentIntentId,
      purchasedAt: serverTimestamp(),
      isCustom: isCustomRing
    });

    return { success: true, price: price };
  } catch (error) {
    console.error('Error purchasing ring:', error);
    return { success: false, error: error.message };
  }
};

// Grant admin special ring
export const grantAdminRing = async (userId, ringId, adminUserId) => {
  try {
    let ringData = ALL_RINGS[ringId];
    let isCustomRing = false;
    
    // If not in default rings, check custom admin rings
    if (!ringData) {
      const customRingRef = doc(db, 'customRings', ringId);
      const customRingDoc = await getDoc(customRingRef);
      if (customRingDoc.exists()) {
        ringData = customRingDoc.data();
        isCustomRing = true;
      }
    }
    
    if (!ringData) {
      return { success: false, reason: 'ring_not_found', message: 'Ring not found' };
    }
    
    // For default rings, check if it's admin special category
    if (!isCustomRing && ringData.category !== RING_CATEGORIES.ADMIN_SPECIAL) {
      return { success: false, reason: 'invalid_category', message: 'Ring is not an admin special ring' };
    }

    const ringRef = doc(db, 'users', userId, 'rings', ringId);
    const ringDoc = await getDoc(ringRef);
    
    // Don't grant if already owned
    if (ringDoc.exists()) {
      return { success: false, reason: 'already_owned', message: 'User already owns this ring' };
    }

    // Grant the ring
    await setDoc(ringRef, {
      ringId,
      ...ringData,
      unlockedAt: serverTimestamp(),
      source: 'admin_grant',
      grantedBy: adminUserId,
      isCustom: isCustomRing
    });

    return { success: true, message: 'Ring granted successfully' };
  } catch (error) {
    console.error('Error granting admin ring:', error);
    return { success: false, reason: 'error', message: error.message };
  }
};

// Generic function to grant a ring to a user

// Check for newly unlocked milestone rings
export const checkForNewMilestoneRings = async (userId, stats) => {
  try {
    const ownedRings = await getUserRings(userId);
    const ownedRingIds = new Set(ownedRings.map(r => r.id));
    const newlyUnlockedRings = [];

    for (const [ringId, ring] of Object.entries(ALL_RINGS)) {
      if (ring.category === RING_CATEGORIES.MILESTONE && 
          !ownedRingIds.has(ringId) && 
          ring.unlockCondition && 
          ring.unlockCondition(stats)) {
        
        const wasUnlocked = await unlockMilestoneRing(userId, ringId);
        if (wasUnlocked) {
          newlyUnlockedRings.push(ring);
        }
      }
    }

    return newlyUnlockedRings;
  } catch (error) {
    console.error('Error checking for new milestone rings:', error);
    return [];
  }
};

// Get rings by category
export const getRingsByCategory = (ownedRings) => {
  const categorized = {};
  
  for (const category of Object.values(RING_CATEGORIES)) {
    categorized[category] = [];
  }

  ownedRings.forEach(ring => {
    const ringData = ALL_RINGS[ring.id];
    if (ringData && categorized[ringData.category]) {
      categorized[ringData.category].push({
        ...ring,
        ...ringData
      });
    }
  });

  return categorized;
};

// Get available rings for purchase
export const getAvailableRingsForPurchase = async (userId) => {
  try {
    const ownedRings = await getUserRings(userId);
    const ownedRingIds = new Set(ownedRings.map(r => r.id));
    
    const availableRings = [];
    
    // Get default rings
    for (const [ringId, ring] of Object.entries(ALL_RINGS)) {
      if (ring.category === RING_CATEGORIES.PURCHASEABLE && !ownedRingIds.has(ringId)) {
        const rarity = RING_RARITIES[ring.rarity];
        availableRings.push({
          ...ring,
          price: rarity.price,
          rarityData: rarity,
          isCustom: false
        });
      }
    }
    
    // Get custom purchaseable rings
    const customRings = await getCustomPurchaseableRings();
    for (const ring of customRings) {
      if (!ownedRingIds.has(ring.id)) {
        const rarity = RING_RARITIES[ring.rarity];
        availableRings.push({
          ...ring,
          price: ring.price || rarity.price,
          rarityData: rarity,
          isCustom: true
        });
      }
    }
    
    // Sort by set, then by price
    return availableRings.sort((a, b) => {
      if (a.set !== b.set) {
        return (a.set || 'zzz').localeCompare(b.set || 'zzz');
      }
      return a.price - b.price;
    });
  } catch (error) {
    console.error('Error getting available rings for purchase:', error);
    return [];
  }
};

// Add custom admin ring
export const addCustomAdminRing = async (ringData, adminUserId) => {
  try {
    const customRingRef = collection(db, 'customRings');
    const docRef = await addDoc(customRingRef, {
      ...ringData,
      category: RING_CATEGORIES.ADMIN_SPECIAL,
      rarity: 'special',
      createdBy: adminUserId,
      createdAt: serverTimestamp()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error adding custom admin ring:', error);
    return null;
  }
};

// Add custom purchaseable ring
export const addCustomPurchaseableRing = async (ringData, adminUserId) => {
  try {
    const customRingRef = collection(db, 'customPurchaseableRings');
    const docRef = await addDoc(customRingRef, {
      ...ringData,
      category: RING_CATEGORIES.PURCHASEABLE,
      createdBy: adminUserId,
      createdAt: serverTimestamp(),
      isActive: true
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error adding custom purchaseable ring:', error);
    return null;
  }
};

// Get custom admin rings
export const getCustomAdminRings = async () => {
  try {
    const customRingsRef = collection(db, 'customRings');
    const customRingsSnapshot = await getDocs(customRingsRef);
    
    return customRingsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting custom admin rings:', error);
    return [];
  }
};

// Get custom purchaseable rings
export const getCustomPurchaseableRings = async () => {
  try {
    const customRingsRef = query(
      collection(db, 'customPurchaseableRings'),
      where('isActive', '==', true)
    );
    const customRingsSnapshot = await getDocs(customRingsRef);
    
    return customRingsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting custom purchaseable rings:', error);
    return [];
  }
};

// Toggle custom ring availability
export const toggleCustomRingAvailability = async (ringId, isActive) => {
  try {
    const ringRef = doc(db, 'customPurchaseableRings', ringId);
    await updateDoc(ringRef, {
      isActive,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error toggling ring availability:', error);
    return false;
  }
};

// Get rings by set
export const getRingsBySet = (rings) => {
  const ringsBySet = {};
  
  // Initialize all sets
  Object.values(RING_SETS).forEach(set => {
    ringsBySet[set] = [];
  });
  ringsBySet.unset = []; // For rings without a set
  
  rings.forEach(ring => {
    const set = ring.set || 'unset';
    if (!ringsBySet[set]) {
      ringsBySet[set] = [];
    }
    ringsBySet[set].push(ring);
  });
  
  return ringsBySet;
};

// Helper function to get all rings (both default and custom)
export const getAllRings = async () => {
  try {
    const customRings = await getCustomPurchaseableRings();
    const allRings = { ...ALL_RINGS };
    
    // Add custom rings to the collection
    customRings.forEach(ring => {
      allRings[ring.id] = ring;
    });
    
    return allRings;
  } catch (error) {
    console.error('Error getting all rings:', error);
    return ALL_RINGS;
  }
};


// ==============================================
// CUSTOM COLLECTION MANAGEMENT FUNCTIONS
// ==============================================

export const getCustomCollections = async () => {
  try {
    const collectionsQuery = query(collection(db, 'customCollections'));
    const collectionsSnapshot = await getDocs(collectionsQuery);
    return collectionsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting custom collections:', error);
    return [];
  }
};

export const createCustomCollection = async (collectionData) => {
  try {
    const docRef = await addDoc(collection(db, 'customCollections'), {
      ...collectionData,
      createdAt: serverTimestamp()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating custom collection:', error);
    return null;
  }
};

export const updateCustomCollection = async (collectionId, updateData) => {
  try {
    const collectionRef = doc(db, 'customCollections', collectionId);
    await updateDoc(collectionRef, updateData);
    return true;
  } catch (error) {
    console.error('Error updating custom collection:', error);
    return false;
  }
};

export const deleteCustomCollection = async (collectionId) => {
  try {
    await deleteDoc(doc(db, 'customCollections', collectionId));
    return true;
  } catch (error) {
    console.error('Error deleting custom collection:', error);
    return false;
  }
};

export const toggleCustomCollectionAvailability = async (collectionId, isActive) => {
  try {
    const collectionRef = doc(db, 'customCollections', collectionId);
    await updateDoc(collectionRef, { isActive });
    return true;
  } catch (error) {
    console.error('Error toggling collection availability:', error);
    return false;
  }
};

// Function to grant free ring pack to user
export const grantFreeRingPack = async (userId, packId, adminUserId) => {
  try {
    const packRef = doc(db, 'ringPacks', packId);
    const packDoc = await getDoc(packRef);
    
    if (!packDoc.exists()) {
      throw new Error('Ring pack not found');
    }
    
    const packData = packDoc.data();
    
    // Check which rings user doesn't have
    const userRings = await getUserRings(userId);
    const userRingIds = userRings.map(ring => ring.ringId);
    const newRings = packData.ringIds.filter(ringId => !userRingIds.includes(ringId));
    
    // Grant new rings
    for (const ringId of newRings) {
      await grantRingToUser(userId, ringId, 'admin_grant', {
        grantedBy: adminUserId,
        packId: packId,
        packName: packData.name
      });
    }
    
    // Record the grant
    await addDoc(collection(db, 'ringPackGrants'), {
      userId,
      packId,
      packName: packData.name,
      ringsGranted: newRings,
      grantedBy: adminUserId,
      grantedAt: serverTimestamp()
    });
    
    return {
      success: true,
      newRings: newRings,
      duplicates: packData.ringIds.length - newRings.length
    };
  } catch (error) {
    console.error('Error granting free ring pack:', error);
    throw error;
  }
};

// Create custom ring set/collection
export const createCustomRingSet = async (setData, adminUserId) => {
  try {
    console.log('Creating custom ring set:', { setData, adminUserId });
    
    if (!adminUserId) {
      throw new Error('Admin user ID is required');
    }
    
    if (!setData.name || !setData.name.trim()) {
      throw new Error('Ring set name is required');
    }
    
    const setId = setData.id || setData.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    
    const customSetRef = doc(db, 'customRingSets', setId);
    const setDocData = {
      id: setId,
      name: setData.name,
      description: setData.description || '',
      icon: setData.icon || '💍',
      createdBy: adminUserId,
      createdAt: serverTimestamp(),
      active: true
    };
    
    console.log('Setting document:', setDocData);
    await setDoc(customSetRef, setDocData);
    console.log('Custom ring set created successfully');
    
    return { success: true, setId };
  } catch (error) {
    console.error('Error creating custom ring set:', error);
    console.error('Error details:', {
      code: error.code,
      message: error.message,
      stack: error.stack
    });
    throw error;
  }
};

// Create ring pack with multiple rings
export const createRingPack = async (packData, adminUserId) => {
  try {
    const packRef = doc(collection(db, 'ringPacks'));
    const packDocData = {
      name: packData.name,
      description: packData.description,
      set: packData.set,
      price: packData.price,
      rings: packData.rings, // Array of ring objects with their individual data
      createdBy: adminUserId,
      createdAt: serverTimestamp(),
      active: packData.active !== false
    };
    
    await setDoc(packRef, packDocData);
    
    // Add each ring in the pack to customPurchaseableRings
    for (const ring of packData.rings) {
      const ringRef = doc(db, 'customPurchaseableRings', ring.id);
      await setDoc(ringRef, {
        ...ring,
        packId: packRef.id,
        packName: packData.name,
        createdBy: adminUserId,
        createdAt: serverTimestamp(),
        active: packData.active !== false
      });
    }
    
    return { success: true, packId: packRef.id };
  } catch (error) {
    console.error('Error creating ring pack:', error);
    throw error;
  }
};

// Purchase ring pack (real currency)
export const purchaseRingPack = async (userId, packId, paymentIntentId = null) => {
  try {
    const packRef = doc(db, 'ringPacks', packId);
    const packDoc = await getDoc(packRef);
    
    if (!packDoc.exists()) {
      return { success: false, error: 'Pack not found' };
    }
    
    const packData = packDoc.data();
    
    if (!packData.active) {
      return { success: false, error: 'Pack not available' };
    }
    
    // Check which rings user doesn't have
    const userRings = await getUserRings(userId);
    const userRingIds = userRings.map(ring => ring.ringId);
    const newRings = packData.rings.filter(ring => !userRingIds.includes(ring.id));
    
    if (newRings.length === 0) {
      return { success: false, error: 'You already own all rings in this pack' };
    }
    
    // Calculate partial price for remaining rings
    const pricePerRing = packData.price / packData.rings.length;
    const actualPrice = parseFloat((pricePerRing * newRings.length).toFixed(2));
    
    // Free packs don't require payment
    if (actualPrice === 0 || packData.price === 0) {
      // Grant new rings
      for (const ring of newRings) {
        await grantRingToUser(userId, ring.id, 'free_pack_claim', {
          packId: packId,
          packName: packData.name,
          purchasePrice: 0,
          ringData: ring
        });
      }
      
      return { 
        success: true, 
        newRings: newRings.length,
        duplicates: packData.rings.length - newRings.length,
        actualPrice: 0
      };
    }
    
    // For paid packs, validate payment intent
    if (!paymentIntentId) {
      return { success: false, error: 'Payment required', price: actualPrice };
    }
    
    // Grant new rings
    for (const ring of newRings) {
      await grantRingToUser(userId, ring.id, 'pack_purchase', {
        packId: packId,
        packName: packData.name,
        purchasePrice: actualPrice,
        paymentIntentId: paymentIntentId,
        ringData: ring
      });
    }
    
    // Log the transaction
    await addDoc(collection(db, 'ringPackPurchases'), {
      userId,
      packId,
      packName: packData.name,
      price: actualPrice,
      ringsGranted: newRings.length,
      paymentIntentId,
      purchasedAt: serverTimestamp()
    });
    
    return { 
      success: true, 
      newRings: newRings.length,
      duplicates: packData.rings.length - newRings.length,
      actualPrice
    };
  } catch (error) {
    console.error('Error purchasing ring pack:', error);
    return { success: false, error: error.message };
  }
};

// Get all ring packs
export const getRingPacks = async () => {
  try {
    const packsRef = collection(db, 'ringPacks');
    const packsSnapshot = await getDocs(packsRef);
    
    const packs = [];
    packsSnapshot.forEach(doc => {
      packs.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return packs.filter(pack => pack.active);
  } catch (error) {
    console.error('Error getting ring packs:', error);
    return [];
  }
};

// Toggle ring pack availability
export const toggleRingPackAvailability = async (packId, isActive) => {
  try {
    const packRef = doc(db, 'ringPacks', packId);
    await updateDoc(packRef, {
      active: isActive,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error toggling ring pack availability:', error);
    return false;
  }
};

// Delete ring pack
export const deleteRingPack = async (packId) => {
  try {
    const packRef = doc(db, 'ringPacks', packId);
    await deleteDoc(packRef);
    return true;
  } catch (error) {
    console.error('Error deleting ring pack:', error);
    return false;
  }
};

// Helper function to grant ring to user with source tracking
export const grantRingToUser = async (userId, ringId, source, metadata = {}) => {
  try {
    const ringRef = doc(db, 'users', userId, 'rings', ringId);
    
    // Check if already owned
    const ringDoc = await getDoc(ringRef);
    if (ringDoc.exists()) {
      console.log(`User ${userId} already owns ring ${ringId}`);
      return { success: false, reason: 'already_owned' };
    }
    
    // Get ring data
    let ringData = ALL_RINGS[ringId];
    if (!ringData) {
      // Check custom rings
      const customRingRef = doc(db, 'customPurchaseableRings', ringId);
      const customRingDoc = await getDoc(customRingRef);
      if (customRingDoc.exists()) {
        ringData = customRingDoc.data();
      }
    }
    
    if (!ringData) {
      throw new Error(`Ring data not found for ${ringId}`);
    }
    
    // Grant the ring
    await setDoc(ringRef, {
      ringId,
      ...ringData,
      unlockedAt: serverTimestamp(),
      source,
      ...metadata
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error granting ring to user:', error);
    return { success: false, reason: 'error', error: error.message };
  }
};

// Clean up basic_border rings from users (run once to migrate)
export const removeBasicBorderFromAllUsers = async () => {
  try {
    console.log('Starting cleanup of basic_border rings...');
    const usersRef = collection(db, 'users');
    const usersSnapshot = await getDocs(usersRef);
    
    const cleanupPromises = [];
    
    usersSnapshot.forEach(userDoc => {
      const userId = userDoc.id;
      const userData = userDoc.data();
      
      // Check if user has basic_border as active ring
      if (userData.activeRing === 'basic_border') {
        console.log(`Removing basic_border from user ${userId} active ring`);
        cleanupPromises.push(
          updateDoc(userDoc.ref, { activeRing: null })
        );
      }
      
      // Remove basic_border from user's rings collection
      const basicBorderRingRef = doc(db, 'users', userId, 'rings', 'basic_border');
      cleanupPromises.push(
        deleteDoc(basicBorderRingRef).catch(() => {
          // Ignore if document doesn't exist
        })
      );
    });
    
    await Promise.all(cleanupPromises);
    console.log('Cleanup completed!');
    
    return { success: true, usersProcessed: usersSnapshot.size };
  } catch (error) {
    console.error('Error during cleanup:', error);
    return { success: false, error: error.message };
  }
};